/**
 * The daemon's one local server (RFC 0331 3a).
 *
 * Sync, git and terminals are paths on one port, and the project a request
 * means is in the path. Before this the window knew two fixed ports, which
 * belonged to whichever project the daemon happened to mount first, so a
 * second project's diff and shell were read from the first project's checkout.
 */

const env = (
  import.meta as unknown as { env?: Record<string, string | undefined> }
).env

/**
 * Where the daemon listens. VITE_LOCAL_SERVER moves it by hand; otherwise the
 * port the desktop shell published for sync (`__modLocalSync`, RFC 0310),
 * which is the same server. Without that, a second Mod beside the main one
 * (RFC 0341) asked the MAIN daemon for its git, terminals and folders.
 * Read on each call: the shell publishes after the page has loaded.
 */
export function localServer(): string {
  if (env?.VITE_LOCAL_SERVER) return env.VITE_LOCAL_SERVER
  const offer = (globalThis as { __modLocalSync?: { url?: unknown } })
    .__modLocalSync
  const port =
    typeof offer?.url === 'string'
      ? /^ws:\/\/127\.0\.0\.1:(\d+)\/sync$/.exec(offer.url)?.[1]
      : undefined
  return port ? `http://127.0.0.1:${port}` : 'http://localhost:7419'
}

/**
 * Whether this window has a daemon to ask: the desktop shell published one, or
 * an endpoint was set by hand. The web window has neither, and a fetch to the
 * localhost fallback there only earns "Failed to fetch".
 */
export function hasLocalServer(): boolean {
  if (env?.VITE_LOCAL_SERVER || env?.VITE_VCS_ENDPOINT) return true
  const offer = (globalThis as { __modLocalSync?: { url?: unknown } })
    .__modLocalSync
  return typeof offer?.url === 'string'
}

/**
 * A project's git or terminal endpoint. VITE_VCS_ENDPOINT and
 * VITE_TERM_ENDPOINT still win, for a hand-run `vcs-serve` or `term-serve`,
 * which serves one checkout and knows no projects.
 */
export function projectEndpoint(
  projectId: string,
  kind: 'git' | 'term',
  sourceId?: string,
): string {
  const override =
    kind === 'git' ? env?.VITE_VCS_ENDPOINT : env?.VITE_TERM_ENDPOINT
  if (override) return override
  const source = sourceId ? `/sources/${encodeURIComponent(sourceId)}` : ''
  return `${localServer()}/projects/${encodeURIComponent(projectId)}${source}/${kind}`
}
